import { Weddings } from '@bridebook/models';
import type { IWedding_RSVP } from '@bridebook/models/source/models/Weddings.types';
import {
  type QueryClient,
  type UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSelector } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { useCallback } from 'react';

export const WEDDING_RSVP_ROOT_QUERY_KEY = 'wedding-rsvp';

export const useWeddingRsvp = <TSelected = IWedding_RSVP | null>(
  options?: Partial<UseQueryOptions<IWedding_RSVP | null, Error, TSelected>>,
) => {
  const weddingId = useSelector(getWeddingId);
  const { data, isLoading, isError, error } = useQuery<IWedding_RSVP | null, Error, TSelected>(
    [WEDDING_RSVP_ROOT_QUERY_KEY, 'single', weddingId],
    async () => {
      const wedding = await Weddings._.getById(weddingId).get();
      return wedding.rsvp ?? null;
    },
    {
      enabled: !!weddingId,
      cacheTime: Infinity, // realtime updates we can cache forever
      staleTime: Infinity,
      ...options,
    },
  );

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

export const updateWeddingRsvpCache = (
  queryClient: QueryClient,
  weddingId: string,
  weddingRsvp: Partial<IWedding_RSVP> | undefined,
) => {
  queryClient.setQueryData<Partial<IWedding_RSVP> | undefined>(
    [WEDDING_RSVP_ROOT_QUERY_KEY, 'single', weddingId],
    weddingRsvp,
  );
};

export const useInvalidateWeddingRsvp = () => {
  const queryClient = useQueryClient();
  const invalidate = useCallback(
    async (weddingId: string) => {
      if (weddingId) {
        await queryClient.invalidateQueries([WEDDING_RSVP_ROOT_QUERY_KEY, 'single', weddingId]);
      }
    },
    [queryClient],
  );

  return { invalidate };
};
